import React, { useContext, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  EditIcon,
  HeadingText,
  TextInput,
  BodyText,
  LoadingSpinner
} from '@paypalcorp/pp-react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import { changeAddress, emailSubmit, getAddressSummary, removeAllChildren } from './helpers';
import ShippingAddressForm from '../ShippingAddress';
import IntegrationPageSectionContext from '../../IntegrationPageSection/StateManagement/IntegrationPageSectionContext';
import TestPanelContext from '../../IntegrationPageSection/StateManagement/TestPanelContext';
import clientLogger from '../../../lib/logger';
import styles from '../Fastlane.module.less';
import BillingAddressForm from '../BillingAddress';
import FlexiblePaymentSection from './FlexiblePaymentSection';
import QuickStartPaymentSection from './QuickStartPaymentSection';
import { captureAuthorize } from '../../../util/sdkCallbacks';
import { isUserLoggedInSelector } from '../../../store/App/selectors';
import { trackButtonClick, trackModalShown } from '../../../lib/fpti';

export default function Fastlane({
  paymentWatermark,
  paymentComponent,
  cardComponent,
  identity,
  profile,
  type
}) {
  const csrf = useSelector((state) => state.app.csrfToken);
  const [activeSection, setActiveSection] = useState(0);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [billingAddress, setBillingAddress] = useState(null);
  const [enableShipping, setEnableShipping] = useState(true);
  const [paymentToken, setPaymentToken] = useState();
  const [authState, setAuthState] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [isProcessing, setIsProcessing] = useState(false); // used for orders calls and etc..
  const appIntegrationType = useSelector((state) => state?.app?.appIntegrationType);

  const {
    SdkConfigurator: { sdkSettings: { intent, clientId } = {} },
    OrderConfigurator: {
      amount: { value: orderAmount },
      sellerId
    },
    lastCapture: { updateLastCapture },
    fastlaneContext: { isLoading }
  } = useContext(IntegrationPageSectionContext);

  const { updateLogs, toggleTestPanel } = useContext(TestPanelContext);
  const isUserLoggedIn = useSelector(isUserLoggedInSelector);

  const handleAuthorizeOrder = async (response) => {
    const authorizationId = get(response, 'purchase_units[0].payments.authorizations[0].id', '');
    await captureAuthorize({
      product: 'fastlane',
      authorizationId,
      csrfToken: csrf,
      updateLogs,
      updateLastCapture,
      clientId: isUserLoggedIn ? clientId : null,
      appIntegrationType
    });
  };

  const handleCheckout = async () => {
    trackButtonClick(`Checkout - ${type}`);
    let memberPaymentToken = paymentToken;
    if (!memberPaymentToken) {
      if (type === 'flexible') {
        memberPaymentToken = await cardComponent.getPaymentToken({
          billingAddress: billingAddress?.address
        });
      } else {
        memberPaymentToken = await paymentComponent.getPaymentToken();
      }
    }
    const body = JSON.stringify({
      paymentToken: memberPaymentToken,
      currencyCode: 'USD',
      amount: orderAmount,
      clientId: isUserLoggedIn ? clientId : null,
      intent: intent ? intent.toUpperCase() : null,
      product: 'fastlane',
      appIntegrationType,
      sellerId,
      ...(enableShipping && { shippingAddress })
    });
    try {
      setIsProcessing(true);
      const createOrderRequest = await fetch('/studio/api/v1/order/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf
        },
        body
      });
      const { response = {}, request = {}, message } = await createOrderRequest.json();
      if (createOrderRequest.status >= 300) {
        clientLogger.cal.error('SERVICE_ERROR', {
          message: `error with create order request. status: ${createOrderRequest.status}`
        });
      }
      delete response.sys;
      updateLogs({
        name: 'Create Order',
        timestamp: new Date().toLocaleString(),
        url: 'POST to v2/checkout/orders',
        request,
        response: message || response
      });
      if (intent === 'authorize') {
        await handleAuthorizeOrder(response);
      }
      setIsProcessing(false);
      toggleTestPanel(true);
    } catch (error) {
      clientLogger.cal.warn('NETWORK_ERROR', {
        message: `error with create order request. message: ${error.message}`
      });
      updateLogs({
        name: 'Create Order - NETWORK ERROR',
        timestamp: new Date().toLocaleString(),
        url: 'POST to v2/checkout/orders',
        response: error
      });
      setIsProcessing(false);
      toggleTestPanel(true);
    }
  };

  const handleEmailSubmit = async () => {
    trackButtonClick('Email Submit');
    const {
      profileAuthState = '',
      profilePaymentToken,
      profileShippingAddress
    } = await emailSubmit(emailAddress, identity);
    if (profilePaymentToken) {
      setPaymentToken(profilePaymentToken);
      if (type === 'flexible') {
        paymentWatermark.render('#payment-watermark');
      }
    } else if (type === 'flexible') {
      cardComponent.render('#card-component');
    }
    if (type !== 'flexible') {
      paymentComponent.render('#payment-component');
    }
    if (profileShippingAddress) {
      setShippingAddress(profileShippingAddress);
    }
    if (profileAuthState === 'succeeded') {
      setAuthState('succeeded');
      trackModalShown('Shipping/Payment Options');
      if (profilePaymentToken && type === 'flexible') {
        setActiveSection(3);
      } else {
        setActiveSection(2);
      }
    } else {
      setActiveSection(1);
    }
  };

  const handleEmailEdit = () => {
    trackButtonClick('Edit Email');
    setShippingAddress(null);
    setActiveSection(0);
    setAuthState('');
    setPaymentToken(null);
    setBillingAddress(null);

    // remove all children in case rendered as a part of previous flow
    removeAllChildren(type === 'flexible' ? 'card-component' : 'payment-component');
  };

  const handleShippingAddressSubmit = async (address) => {
    if (type !== 'flexible') {
      await paymentComponent.setShippingAddress(address);
    }
    if (authState === 'succeeded' && type === 'flexible') {
      setActiveSection(3);
    } else {
      setActiveSection(2);
    }
    setShippingAddress(address);
    trackButtonClick('Shipping Address Submit');
  };

  const handleBillingAddressSubmit = (address) => {
    setBillingAddress(address);
    trackButtonClick('Billing Address Submit');
    setActiveSection(3);
  };

  return (
    <div className={styles.fastlane_preview_container}>
      {(isLoading || isProcessing) && (
        <div className={styles.preview_loader_container}>
          <LoadingSpinner className={styles.preview_loader} screenReaderText="loading" size="lg" />
        </div>
      )}
      <div className={styles.section_heading}>
        <HeadingText size="sm">Customer</HeadingText>
        {activeSection > 0 && (
          <Button
            iconComponent={EditIcon}
            secondary
            className={styles.edit_button}
            onClick={handleEmailEdit}
            size="sm"
          >
            Edit
          </Button>
        )}
      </div>
      <section id="fastlane-identity-section">
        <section id="fastlane-email-section">
          {activeSection === 0 ? (
            <TextInput
              id="email-input"
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              autoComplete="email"
              placeholder="Email"
              className={styles.identity_section_input}
              disabled={activeSection !== 0}
            />
          ) : (
            <BodyText className={styles.identity_section_input}>{emailAddress}</BodyText>
          )}
          {activeSection === 0 && (
            <Button className={styles.continue_button} onClick={handleEmailSubmit}>
              Continue
            </Button>
          )}
        </section>
        <div id="watermark-container" style={{ display: activeSection === 0 ? 'block' : 'none' }} />
      </section>
      <Divider className="divider" />
      <div className={styles.section_heading}>
        <HeadingText size="sm">Shipping</HeadingText>
        {!authState && activeSection > 1 && (
          <Button
            iconComponent={EditIcon}
            className={styles.edit_button}
            secondary
            size="sm"
            onClick={() => setActiveSection(1)}
          >
            Edit
          </Button>
        )}
        {authState === 'succeeded' && shippingAddress && activeSection !== 1 && (
          <Button
            iconComponent={EditIcon}
            secondary
            size="sm"
            className={styles.edit_button}
            onClick={() => changeAddress(profile, paymentComponent, setShippingAddress)}
          >
            Edit
          </Button>
        )}
      </div>
      <section
        className={classNames({
          [styles.section_summary]: shippingAddress && activeSection !== 1,
          [styles.shipping_section]: !shippingAddress || activeSection === 1
        })}
      >
        {activeSection === 1 && (
          <Checkbox
            checked={enableShipping}
            className={styles.fastlane_shipping_checkbox}
            onChange={() => {
              const enableShippingUpdate = !enableShipping;
              setEnableShipping(enableShippingUpdate);
              if (!enableShippingUpdate) {
                setShippingAddress(null);
              } else {
                setActiveSection(1);
              }
            }}
            label="This purchase requires shipping"
          />
        )}
        {shippingAddress && activeSection !== 1 && (
          <BodyText className={styles.address_summary_text}>
            {getAddressSummary(shippingAddress)}
          </BodyText>
        )}
        {activeSection === 1 && (
          <>
            {enableShipping && (
              <ShippingAddressForm
                shippingAddress={shippingAddress || {}}
                onSubmit={handleShippingAddressSubmit}
              />
            )}
            {!enableShipping && (
              <div className={styles.cta_button_container}>
                <Button
                  primary
                  onClick={() => {
                    handleShippingAddressSubmit(null);
                    setActiveSection(2);
                  }}
                >
                  Continue
                </Button>
              </div>
            )}
          </>
        )}
      </section>
      {!paymentToken && type === 'flexible' && (
        <section>
          <Divider className="divider" />
          <div className={styles.section_heading}>
            <HeadingText size="sm">Billing</HeadingText>
            {billingAddress && activeSection !== 2 && (
              <Button
                iconComponent={EditIcon}
                secondary
                size="sm"
                className={styles.edit_button}
                onClick={() => setActiveSection(2)}
              >
                Edit
              </Button>
            )}
          </div>
          {billingAddress && activeSection !== 2 && (
            <div className={styles.section_summary}>
              <BodyText className={styles.address_summary_text}>
                {getAddressSummary(billingAddress)}
              </BodyText>
            </div>
          )}
          {activeSection === 2 && (
            <section>
              <BillingAddressForm
                billingAddress={billingAddress?.address || {}}
                onSubmit={handleBillingAddressSubmit}
              />
            </section>
          )}
        </section>
      )}
      <Divider className="divider" />
      {type === 'flexible' ? (
        <FlexiblePaymentSection
          activeSection={activeSection}
          paymentToken={paymentToken}
          profile={profile}
          setPaymentToken={setPaymentToken}
        />
      ) : (
        <QuickStartPaymentSection activeSection={activeSection} />
      )}
      {(activeSection === 3 || (type !== 'flexible' && activeSection === 2)) && (
        <div className={styles.cta_button_container}>
          <Button id="checkout-button" primary onClick={handleCheckout}>
            Checkout
          </Button>
        </div>
      )}
    </div>
  );
}

Fastlane.propTypes = {
  paymentWatermark: PropTypes.object,
  paymentComponent: PropTypes.object,
  cardComponent: PropTypes.object,
  identity: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  type: PropTypes.string
};

Fastlane.defaultProps = {
  type: '',
  cardComponent: null,
  paymentComponent: null,
  paymentWatermark: null
};
